import React from 'react'
import chroma from 'chroma-js'
import Plot from '~/prix/react/components/plot'
import { getAlertColor } from '~/packages/general/variable/spaceTime.data'
import { MetricsRow, MetricValueWrap, Wrapper } from '../styles'
import { axisBase } from '../utils/axis'
import { formatBR } from '../utils/format'
import {
  OutbreakDetectionPlotPayload,
  OutbreakDetectionPlotProps,
  OutbreakDetectionRange,
} from '../visualization.types'

function formatPValue(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—'
  if (value < 0.001) return '< 0,001'
  return value.toLocaleString('pt-BR', {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  })
}

function formatMetricValue(value: number | null | undefined, unit?: string) {
  if (value == null || !Number.isFinite(value)) return '—'
  return `${formatBR(value)}${unit ?? ''}`
}

function normalizeDetectionIndices(
  detections: OutbreakDetectionRange[],
  totalPoints: number,
): number[] {
  const indexes = new Set<number>()

  for (const detection of detections) {
    const startIndex = Math.max(0, detection.startIndex)
    const endIndex = Math.min(totalPoints - 1, detection.endIndex)

    for (let index = startIndex; index <= endIndex; index += 1) {
      indexes.add(index)
    }
  }

  return Array.from(indexes).sort((left, right) => left - right)
}

function buildDetectionShapes(
  detections: OutbreakDetectionRange[],
  fillColor: string,
): Partial<Plotly.Shape>[] {
  return detections.map(detection => ({
    type: 'rect',
    xref: 'x',
    yref: 'paper',
    x0: detection.start,
    x1: detection.end,
    y0: 0,
    y1: 1,
    fillcolor: fillColor,
    line: {
      width: 0,
    },
    layer: 'below',
  }))
}

export default function OutbreakDetectionPlot({
  visualization,
  isExpanded,
  alert,
}: OutbreakDetectionPlotProps) {
  const payload = visualization.payload as OutbreakDetectionPlotPayload

  if (!Array.isArray(payload.x) || !Array.isArray(payload.observed) || !payload.x.length) {
    return null
  }

  const totalPoints = Math.min(payload.x.length, payload.observed.length)
  const x = payload.x.slice(0, totalPoints)
  const observed = payload.observed.slice(0, totalPoints)
  const generated =
    Array.isArray(payload.generated) && payload.generated.length === totalPoints
      ? payload.generated
      : undefined
  const pValues =
    Array.isArray(payload.pValues) && payload.pValues.length === totalPoints ? payload.pValues : []
  const detections = Array.isArray(payload.detections) ? payload.detections : []
  const alertColor = alert ? getAlertColor(alert) : '#dc2626'
  const detectionFill = chroma(alertColor)
    .alpha(isExpanded ? 0.16 : 0.12)
    .css()
  const outbreakIndices = normalizeDetectionIndices(detections, totalPoints)
  const outbreakX = outbreakIndices.map(index => x[index])
  const outbreakY = outbreakIndices.map(index => observed[index])
  const hasGenerated = Array.isArray(generated) && generated.some(value => value !== null)
  const compactHeight = 72
  const expandedHeight = 260

  if (!isExpanded) {
    const compactData: Plotly.Data[] = [
      {
        x,
        y: observed,
        type: 'scatter',
        mode: 'lines',
        name: 'Observado',
        line: {
          color: '#2563eb',
          width: 2,
          shape: 'linear',
        },
        hovertemplate: 'Data: %{x}<br>Valor: %{y}<extra></extra>',
        showlegend: false,
      },
      {
        x: outbreakX,
        y: outbreakY,
        type: 'scatter',
        mode: 'markers',
        name: 'Detecção',
        marker: {
          color: alertColor,
          size: 6,
        },
        hovertemplate: 'Data: %{x}<br>Valor: %{y}<br>Janela detectada<extra></extra>',
        showlegend: false,
      },
    ]

    const compactLayout: Partial<Plotly.Layout> = {
      height: compactHeight,
      margin: { l: 0, r: 0, t: 0, b: 0 },
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      showlegend: false,
      xaxis: {
        visible: false,
        type: 'date',
      },
      yaxis: {
        visible: false,
      },
      shapes: buildDetectionShapes(detections, detectionFill),
      hovermode: 'x unified',
      hoverlabel: {
        bgcolor: 'white',
        bordercolor: '#e5e7eb',
        font: { size: 12, color: '#111827', family: 'Figtree, sans-serif' },
      },
    }

    return (
      <Wrapper $height={compactHeight}>
        <Plot data={compactData} layout={compactLayout as Plotly.Layout} />
      </Wrapper>
    )
  }

  const expandedData: Plotly.Data[] = []

  if (hasGenerated) {
    expandedData.push({
      x,
      y: generated as Array<number | null>,
      type: 'scatter',
      mode: 'lines',
      name: 'Esperado',
      line: {
        color: '#94a3b8',
        width: 2,
        dash: 'dash',
      },
      hovertemplate: 'Data: %{x}<br>Esperado: %{y}<extra></extra>',
    })
  }

  expandedData.push(
    {
      x,
      y: observed,
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Observado',
      line: {
        color: '#2563eb',
        width: 2.5,
      },
      marker: {
        color: '#2563eb',
        size: 6,
      },
      hovertemplate: 'Data: %{x}<br>Observado: %{y}<extra></extra>',
    },
    {
      x: outbreakX,
      y: outbreakY,
      type: 'scatter',
      mode: 'markers',
      name: 'Janela detectada',
      marker: {
        color: alertColor,
        size: 10,
        line: {
          color: '#ffffff',
          width: 1.5,
        },
      },
      hovertemplate: 'Data: %{x}<br>Observado: %{y}<br>Janela detectada<extra></extra>',
    },
  )

  if (pValues.length) {
    expandedData.push(
      {
        x,
        y: pValues,
        type: 'scatter',
        mode: 'lines',
        name: 'Valor-p',
        yaxis: 'y2',
        line: {
          color: '#7c3aed',
          width: 2,
        },
        hovertemplate: 'Data: %{x}<br>Valor-p: %{y:.3f}<extra></extra>',
      },
      {
        x: [x[0], x[x.length - 1]],
        y: [payload.alpha, payload.alpha],
        type: 'scatter',
        mode: 'lines',
        name: 'Limiar α',
        yaxis: 'y2',
        line: {
          color: alertColor,
          width: 2,
          dash: 'dash',
        },
        hovertemplate: 'Limiar α: %{y:.3f}<extra></extra>',
      },
    )
  }

  const layout: Partial<Plotly.Layout> = {
    height: expandedHeight,
    margin: { l: 56, r: 58, t: 8, b: 40 },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: 1.02,
      xanchor: 'left',
      x: 0,
      font: {
        size: 11,
        color: '#374151',
        family: 'Figtree, sans-serif',
      },
    },
    xaxis: {
      ...axisBase,
      type: 'date',
      tickformat: '%d/%m',
      title: '',
    },
    yaxis: {
      ...axisBase,
      title: '',
      rangemode: 'tozero',
      tickformat: '~s',
    },
    yaxis2: {
      ...axisBase,
      overlaying: 'y',
      side: 'right',
      range: [0, 1],
      showgrid: false,
      tickformat: '.2f',
      title: '',
    },
    hovermode: 'x unified',
    hoverlabel: {
      bgcolor: 'white',
      bordercolor: '#e5e7eb',
      font: { size: 12, color: '#111827', family: 'Figtree, sans-serif' },
    },
    shapes: buildDetectionShapes(detections, detectionFill),
  }

  const metricVariant =
    payload.meta?.pValue != null && payload.meta.pValue <= payload.alpha ? 'negative' : 'flat'

  return (
    <>
      <MetricsRow>
        <MetricValueWrap $variant={metricVariant}>
          p-valor {formatPValue(payload.meta?.pValue)}
        </MetricValueWrap>
        <MetricValueWrap $variant='flat'>α {formatPValue(payload.alpha)}</MetricValueWrap>
        <MetricValueWrap $variant='flat'>
          janela {formatMetricValue(payload.meta?.windowSize, ' dias')}
        </MetricValueWrap>
        <MetricValueWrap $variant='flat'>
          defasagem {formatMetricValue(payload.meta?.phase, ' dias')}
        </MetricValueWrap>
        <MetricValueWrap $variant={metricVariant}>
          confiança {formatMetricValue(payload.meta?.confidence, '%')}
        </MetricValueWrap>
      </MetricsRow>
      <Wrapper $height={expandedHeight}>
        <Plot data={expandedData} layout={layout as Plotly.Layout} />
      </Wrapper>
    </>
  )
}
